import axios, { AxiosRequestConfig } from "axios";
import ApiService from "./apiService";
import EncompassService from "./encompassService";
import AuthService from "./authService";
import { ISaveOrderDetailRequest } from "../models/ISaveOrderDetailRequest";
import { transformApiResponse, transformApiRequest } from "../utils/caseTransform";

const useProxy = false; // Set to true to use proxy for CORS issues

/*
 * The Plugin service is used to get, post and put any of the calls to the 
 * plugin api which is used to handling a lot of the configuration area
 */
class PluginService {

    private apiService: ApiService;
    private epcApiService: ApiService;
    constructor() {
        this.apiService = ApiService.getInstance(EncompassService.getPluginEndpoint() as string);
        // Create a new service instance without /concept for configuration endpoints as we don't want to create a multiple endpoints for same functionality
        const baseEndpoint = EncompassService.getExpressionValidationEndpoint() as string;
        const configEndpoint = baseEndpoint.replace('/concept', '');
        this.epcApiService = ApiService.getInstance(configEndpoint);
    }

    public async getConfig<T>(token: string | null, route: string, configurationId: string): Promise<T> {

        try {

            if (!token && useProxy) {

                const authService = new AuthService();
                token = (await authService.getToken()).access_token;
            }

            const config: AxiosRequestConfig = {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                params: {
                    configurationId: configurationId,
                }
            }

            let response;
            if (useProxy)
                response = await axios.get(`${EncompassService.getPluginEndpoint()}/v1/configuration/${route}?token=${encodeURIComponent(token as string)}&configId=${encodeURIComponent(configurationId)}`);
            else
                response = await this.apiService.get(`/v1/configuration/${route}`, config);
            return response.data as T;
        } catch (error) {
            console.error('Error Fetching Config:', error);
            throw error;
        }
    }

    public async saveConfig<T>(token: string | null, route: string, configurationId: string, body: T): Promise<T> {

        try {

            if (!token && useProxy) {
                const authService = new AuthService();
                token = (await authService.getToken()).access_token;
            }

            if (!token || !configurationId || !body)
                throw new Error("Token, ConfigurationId and Body must be present to save configuration.");

            const config: AxiosRequestConfig = {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }

            let response;
            if (useProxy) // Warning: Proxy may be serializing the data automatically 
                response = await axios.put(`${EncompassService.getPluginEndpoint()}/v1/configuration/${route}?token=${encodeURIComponent(token as string)}`, body);
            else {
                const requestBody = JSON.stringify(body); // handling serialization - CORS Network Error Issue
                response = await this.apiService.put(`/v1/configuration/${route}`, requestBody, config);
            }
            return response.data as T;
        } catch (error) {
            console.error('Error Saving Config:', error);
            throw error;
        }
    }

    // New method to get Configurations using new rest api endpoint
    public async getEpcApiConfig<T>(token: string | null, route: string, configurationId: string): Promise<T> {

        try {

            if (!token) {

                const authService = new AuthService();
                token = (await authService.getToken()).access_token;
            }

            const config: AxiosRequestConfig = {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                params: {
                    configurationId: configurationId,
                }
            }

            let response = await this.epcApiService.get(`/configuration/${route}`, config);

            // Transform camelCase response to PascalCase to match TypeScript interfaces
            // Once all the configs are moved over to the new api, we can remove this transformation
            const data = response.data as { result: any };
            const transformed = transformApiResponse<T>(data.result);
            return transformed;
        } catch (error) {
            console.error('Error Fetching Config:', error);
            throw error;
        }
    }

    // New method to save Configurations using new rest api endpoint
    public async saveEpcApiConfig<T>(token: string | null, route: string, configurationId: string, body: T): Promise<T> {

        try {

            if (!token) {
                const authService = new AuthService();
                token = (await authService.getToken()).access_token;
            }

            if (!token || !configurationId || !body)
                throw new Error("Token, ConfigurationId and Body must be present to save configuration.");

            const config: AxiosRequestConfig = {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }

            let response = await this.epcApiService.put(`configuration/${route}`, body, config);

            // Transform camelCase response back to PascalCase
            return transformApiResponse<T>(response.data);
        } catch (error) {
            console.error('Error Saving Config:', error);
            throw error;
        }
    }

    public async saveOrderDetails(token: string | null, orderDetailRequest: ISaveOrderDetailRequest): Promise<number> {

        try {

            if (!token) {
                const authService = new AuthService();
                token = (await authService.getToken()).access_token;
            }

            if (!token || !orderDetailRequest)
                throw new Error("Token and OrderDetailRequest must be present to save order details.");

            const config: AxiosRequestConfig = {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }

            const requestBody = JSON.stringify(orderDetailRequest);
            let response = await this.apiService.put(`/v1/order-details`, requestBody, config);
            return response.status;
        } catch (error) {
            console.error('Error Saving Order Details:', error);
            throw error;
        }
    }


    // New method to get Configurations for ASO using new rest api endpoint
    public async getEpcASOConfig<T>(route: string, instanceId: string, configurationId?: string): Promise<T> {

        try {
            const config: AxiosRequestConfig = {
                params: {
                    encompassInstanceId: instanceId,
                    configurationId: configurationId
                }
            }

            let response = await this.epcApiService.get(`/configuration/${route}`, config);

            // Transform camelCase response back to PascalCase
            return transformApiResponse<T>(response.data);
        } catch (error) {
            console.error('Error Fetching Config:', error);
            throw error;
        }
    }
}

export default PluginService;